import { linspace, smoothPointsMultiple } from "./utils.js";

const WORLD_WIDTH = 3000;
const WORLD_HEIGHT = 2000;
const SCALE = 0.8;
const POINTS = Math.round(1250 * SCALE);

let terrain;
export function setTerrain(t) {
  terrain = t;
  window.terrain = t;
}

export function riverInit(W) {
  const len = WORLD_WIDTH;
  const x = linspace(0, len, POINTS);
  const bends = 3;

  // Generate y coordinates with noise
  const y = new Array(POINTS);
  for (let i = 0; i < POINTS; i++) {
    const normalizedX = (x[i] / len) * bends * 2 * Math.PI;
    const sineComponent =
      W * Math.sin(normalizedX) +
      W * 0.5 * Math.sin(normalizedX * 0.5) + // Lower frequency for large curves
      W * 0.25 * Math.sin(normalizedX * 2.0) + // Higher frequency for detail
      W * 0.125 * Math.sin(normalizedX * 4.0); // Even higher for subtle variation
    y[i] = sineComponent;
  }

  // Smooth out just the ends (using percentage of points rather than fixed number)
  const endPoints = Math.floor(POINTS * 0.05);
  for (let i = 0; i < endPoints; i++) {
    const t = i / endPoints;
    y[i] = y[i] * t;
  }
  for (let i = POINTS - endPoints; i < POINTS; i++) {
    const t = (POINTS - i) / endPoints;
    y[i] = y[i] * t;
  }

  const smoothedY = smoothPointsMultiple(y, 3, 5);

  // Center the river in the world space
  const minX = Math.min(...x);
  const maxX = Math.max(...x);
  const minY = Math.min(...smoothedY);
  const maxY = Math.max(...smoothedY);

  // Calculate scaling factors to fit within world bounds while maintaining aspect ratio
  const scaleX = WORLD_WIDTH / (maxX - minX);
  const scaleY = WORLD_HEIGHT / (maxY - minY);
  // the percentage here basically controls the scale
  const scale = Math.min(scaleX, scaleY) * SCALE;

  // Center the river in world space
  const normalizedX = x.map(
    (x) => (x - minX) * scale + (WORLD_WIDTH - (maxX - minX) * scale) / 2,
  );
  const normalizedY = smoothedY.map(
    (y) => (y - minY) * scale + (WORLD_HEIGHT - (maxY - minY) * scale) / 2,
  );

  // Update terrain with river points
  terrain.setRiverPoints(normalizedX, normalizedY);

  return {
    x: normalizedX,
    y: normalizedY,
    g: Array(POINTS).fill(0),
    w: Array(POINTS).fill(0),
    hist: [],
  };
}

export function resampleCurve(x, y, numPoints) {
  const arcLengths = [0];
  let totalLength = 0;

  // Calculate cumulative arc lengths
  for (let i = 1; i < x.length; i++) {
    const dx = x[i] - x[i - 1];
    const dy = y[i] - y[i - 1];
    const segmentLength = Math.sqrt(dx * dx + dy * dy);
    totalLength += segmentLength;
    arcLengths.push(totalLength);
  }

  const newX = [];
  const newY = [];

  // Resample points at equal arc length intervals
  for (let i = 0; i < numPoints; i++) {
    const targetLength = (i / (numPoints - 1)) * totalLength;

    // Binary search for the segment containing this point
    let lo = 0;
    let hi = arcLengths.length - 2;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (arcLengths[mid] <= targetLength) {
        lo = mid;
      } else {
        hi = mid - 1;
      }
    }
    const segmentIndex = lo;

    // Interpolate between points
    const segmentLength =
      arcLengths[segmentIndex + 1] - arcLengths[segmentIndex];
    const t =
      segmentLength === 0
        ? 0
        : (targetLength - arcLengths[segmentIndex]) / segmentLength;

    newX.push(x[segmentIndex] + t * (x[segmentIndex + 1] - x[segmentIndex]));
    newY.push(y[segmentIndex] + t * (y[segmentIndex + 1] - y[segmentIndex]));
  }

  return { x: newX, y: newY };
}

export function resampleRiver(river) {
  const { x, y } = river;
  const { x: newX, y: newY } = resampleCurve(x, y, POINTS);
  river.x = newX;
  river.y = newY;
}

export function extractLoop(points, segmentInfo) {
  const { startR, endR } = segmentInfo;

  const loopPoints = points.slice(startR, endR);

  loopPoints.push(loopPoints[0]);

  return loopPoints;
}

export function removeLoop(river, segmentInfo, currentTime) {
  const { start, end } = segmentInfo;

  const x = [...river.x.slice(0, start + 1), ...river.x.slice(end + 1)];
  const y = [...river.y.slice(0, start + 1), ...river.y.slice(end + 1)];
  if (start > end) debugger;

  const n = 2;
  const cutoff = {
    x: river.x.slice(start + n, end),
    y: river.y.slice(start + n, end),
    time: currentTime,
    coords: "screen",
    type: "cutoff",
  };
  if (cutoff.x.length > 6) {
    river.hist.push(cutoff);

    if (river.hist.length > 500) {
      river.hist.shift();
    }
  }
  river.x = x;
  river.y = y;
  return river;
}

export function slopeVector(x, y, w) {
  const sampleDistance = Math.round(w);
  let scale = 30;
  const SLOPE_THRESHOLD = 0.0001;

  const elevation = terrain.getElevation(x, y);
  const eRight = terrain.getElevation(x + sampleDistance, y);
  const eLeft = terrain.getElevation(x - sampleDistance, y);
  const eDown = terrain.getElevation(x, y + sampleDistance);
  const eUp = terrain.getElevation(x, y - sampleDistance);

  if (
    elevation >= 0.2 ||
    eRight >= 0.2 ||
    eLeft >= 0.2 ||
    eDown >= 0.2 ||
    eUp >= 0.2
  ) {
    scale = scale * 3;
  }

  const gradientX = ((eRight - eLeft) / (2 * sampleDistance)) * scale;
  const gradientY = ((eDown - eUp) / (2 * sampleDistance)) * scale;

  if (
    Math.abs(gradientX) < SLOPE_THRESHOLD &&
    Math.abs(gradientY) < SLOPE_THRESHOLD
  ) {
    return [0, 0];
  }

  return [gradientX, gradientY];
}
